import debounce from 'lodash/debounce'
import { useEffect } from 'react'

import { GUEST_INBOX_V2_EMAILS } from '@new/__global/constants/emailsList'
import { useEmailPermission } from '@new/__global/hooks/useEmailPermission'

import { queryClient } from '@queries/query-client'

import { stayTimelineQueryKeys } from '../queries/queryKeys'
import { TIMELINE_HIDDEN_GRACE_MS } from '../StayTimeline.consts'
import { subscribeToStayTimeline } from '../stores/api/stayTimelineLiveArriere'
import { useRefreshTimelineOnVisible } from '../useRefreshTimelineOnVisible'

interface UseStayTimelineLiveParams {
  bookingId?: string
  isVisible: boolean
}

export const useStayTimelineLive = ({ bookingId, isVisible }: UseStayTimelineLiveParams) => {
  const isBetaTester = useEmailPermission({ allowedEmails: GUEST_INBOX_V2_EMAILS })
  const url: string | undefined = import.meta.env.VITE_ARRIERE_BACKOFFICE_HIVE_URL
  const isEnabled = isBetaTester && isVisible

  useRefreshTimelineOnVisible({ bookingId, isEnabled: !url || !isEnabled })

  useEffect(() => {
    if (!bookingId || !url || !isEnabled) return

    let controller: AbortController | undefined
    let closeTimeout: NodeJS.Timeout
    let wasLive = false

    const invalidateTimeline = () => {
      void queryClient.invalidateQueries({
        queryKey: stayTimelineQueryKeys.bookingConversations(bookingId),
      })
    }

    const refetchTimeline = debounce(invalidateTimeline, 300, { maxWait: 1500 })

    const unsubscribe = () => {
      controller?.abort()
      controller = undefined
      refetchTimeline.cancel()

      if (wasLive) invalidateTimeline()

      wasLive = false
    }

    const subscribe = () => {
      controller = new AbortController()
      subscribeToStayTimeline({
        bookingId,
        url,
        signal: controller.signal,
        onChange: refetchTimeline,
        onLive: isLive => {
          if (isLive !== wasLive) refetchTimeline()
          wasLive = isLive
        },
      })
    }

    const handleVisibilityChange = () => {
      clearTimeout(closeTimeout)

      if (document.visibilityState === 'hidden') {
        closeTimeout = setTimeout(unsubscribe, TIMELINE_HIDDEN_GRACE_MS)
        return
      }

      if (!controller) subscribe()
    }

    if (document.visibilityState !== 'hidden') subscribe()

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(closeTimeout)
      unsubscribe()
    }
  }, [bookingId, isEnabled, url])
}
